import { useRef, useCallback, useEffect } from 'react';
import { PeerConnection } from '@/services/webrtcService';
import { hostVideoSender, guestVideoReceiver } from '@/services/videoTransferService';
import { useConnectionStore } from '@/store/connectionStore';
import { usePlayerStore } from '@/store/playerStore';
import { useRoomStore } from '@/store/roomStore';
import type { SignalingMessage, VideoQuality } from '@/types';

interface UseWebRTCOptions {
  participantId: string | null;
  role: 'host' | 'guest' | null;
  send: (msg: Omit<SignalingMessage, 'timestamp'>) => void;
}

export function useWebRTC({ participantId, role, send }: UseWebRTCOptions) {
  const peerConnections = useRef<Map<string, PeerConnection>>(new Map());
  const peerDisconnectTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const localStreamRef = useRef<MediaStream | null>(null);
  const { setRtcState, setIsRelay } = useConnectionStore();
  const { setStats } = usePlayerStore();

  const sendOffer = useCallback(
    async (pc: PeerConnection, targetId: string) => {
      try {
        const offer = await pc.createOffer();
        if (!offer) return;
        send({
          type: 'OFFER',
          from: participantId ?? undefined,
          payload: { sdp: offer, targetId },
        });
      } catch (e) {
        console.warn('[WebRTC] sendOffer error:', e);
      }
    },
    [participantId, send]
  );

  const createPeerConnection = useCallback(
    (peerId: string, peerRole: 'offerer' | 'answerer') => {
      const existing = peerConnections.current.get(peerId);
      if (existing) return existing;

      const pc = new PeerConnection({
        peerId,
        role: peerRole,
        onTrack: (streams) => {
          const stream = streams[0];
          if (!stream) return;
          const event = new CustomEvent('cinesync:remotestream', {
            detail: { peerId, stream },
          });
          window.dispatchEvent(event);
        },
        onDataChannel: (channel) => {
          if (role === 'host') {
            hostVideoSender.attachChannel(peerId, channel);
          } else {
            guestVideoReceiver.attachChannel(channel);
          }
        },
        onIceCandidate: (candidate) => {
          send({
            type: 'ICE_CANDIDATE',
            from: participantId ?? undefined,
            payload: { candidate: candidate.toJSON(), targetId: peerId },
          });
        },
        onConnectionStateChange: (state) => {
          const stateMap: Record<string, ReturnType<typeof useConnectionStore.getState>['rtcState']> = {
            new: 'connecting',
            connecting: 'connecting',
            connected: 'connected',
            disconnected: 'reconnecting',
            failed: 'failed',
            closed: 'disconnected',
          };
          setRtcState(stateMap[state] ?? 'disconnected');

          // Active Dead-Peer Detection for Host: If a guest drops connection (phone locked, tab closed, crash)
          if (role === 'host') {
            if (state === 'failed' || state === 'disconnected' || state === 'closed') {
              if (!peerDisconnectTimers.current.has(peerId)) {
                const timer = setTimeout(() => {
                  peerDisconnectTimers.current.delete(peerId);
                  const deadPc = peerConnections.current.get(peerId);
                  if (deadPc) {
                    deadPc.close();
                    peerConnections.current.delete(peerId);
                  }
                  useRoomStore.getState().removeParticipant(peerId);
                  send({
                    type: 'PARTICIPANT_LEFT',
                    from: participantId ?? undefined,
                    payload: { participantId: peerId },
                  });
                }, 4000);
                peerDisconnectTimers.current.set(peerId, timer);
              }
            } else if (state === 'connected') {
              const existingTimer = peerDisconnectTimers.current.get(peerId);
              if (existingTimer) {
                clearTimeout(existingTimer);
                peerDisconnectTimers.current.delete(peerId);
              }
            }
          }
        },
        onNegotiationNeeded: () => {
          if (peerRole === 'offerer') {
            sendOffer(pc, peerId);
          }
        },
      });

      peerConnections.current.set(peerId, pc);
      return pc;
    },
    [participantId, send, setRtcState, sendOffer]
  );

  /** Host: add local media stream tracks to all peer connections and renegotiate */
  const setLocalStream = useCallback(
    (stream: MediaStream) => {
      localStreamRef.current = stream;
      peerConnections.current.forEach((pc, peerId) => {
        pc.addTracks(stream);
        sendOffer(pc, peerId);
      });
    },
    [sendOffer]
  );

  /** Host initiates offer to a new guest */
  const initiateOffer = useCallback(
    async (guestId: string) => {
      const pc = createPeerConnection(guestId, 'offerer');
      if (localStreamRef.current) {
        pc.addTracks(localStreamRef.current);
      }
      await sendOffer(pc, guestId);
      return pc;
    },
    [createPeerConnection, sendOffer]
  );

  /** Guest handles incoming offer */
  const handleOffer = useCallback(
    async (hostId: string, sdp: RTCSessionDescriptionInit) => {
      const pc = createPeerConnection(hostId, 'answerer');
      const answer = await pc.handleOffer(sdp);
      send({
        type: 'ANSWER',
        from: participantId ?? undefined,
        payload: { sdp: answer, targetId: hostId },
      });
    },
    [createPeerConnection, participantId, send]
  );

  /** Host handles incoming answer from guest */
  const handleAnswer = useCallback(
    async (guestId: string, sdp: RTCSessionDescriptionInit) => {
      const pc = peerConnections.current.get(guestId);
      if (!pc) return;
      await pc.handleAnswer(sdp);
      pc.startStats((stats) => {
        setStats(stats);
        setIsRelay(stats.connectionType === 'relay');
      });
    },
    [setStats, setIsRelay]
  );

  /** Handle ICE candidate from remote */
  const handleIceCandidate = useCallback(
    async (fromId: string, candidate: RTCIceCandidateInit) => {
      const pc = peerConnections.current.get(fromId);
      if (!pc) return;
      await pc.addIceCandidate(candidate);
    },
    []
  );

  /** Guest: start stats after becoming connected */
  const startGuestStats = useCallback(
    (hostId: string) => {
      const pc = peerConnections.current.get(hostId);
      if (!pc) return;
      pc.startStats((stats) => {
        setStats(stats);
        setIsRelay(stats.connectionType === 'relay');
      });
    },
    [setStats, setIsRelay]
  );

  const closePeer = useCallback((peerId: string) => {
    const timer = peerDisconnectTimers.current.get(peerId);
    if (timer) {
      clearTimeout(timer);
      peerDisconnectTimers.current.delete(peerId);
    }
    if (role === 'host') {
      hostVideoSender.detachChannel(peerId);
    }
    const pc = peerConnections.current.get(peerId);
    if (pc) {
      pc.close();
      peerConnections.current.delete(peerId);
    }
  }, [role]);

  const closeAll = useCallback(() => {
    peerDisconnectTimers.current.forEach((t) => clearTimeout(t));
    peerDisconnectTimers.current.clear();
    if (role === 'host') {
      peerConnections.current.forEach((_, peerId) => hostVideoSender.detachChannel(peerId));
    } else {
      guestVideoReceiver.reset();
    }
    peerConnections.current.forEach((pc) => pc.close());
    peerConnections.current.clear();
    localStreamRef.current = null;
  }, [role]);

  const setPeerQuality = useCallback((peerId: string, quality: VideoQuality) => {
    const pc = peerConnections.current.get(peerId);
    if (pc) {
      pc.setVideoQuality(quality);
    }
  }, []);

  const setAllPeersQuality = useCallback((quality: VideoQuality) => {
    peerConnections.current.forEach((pc) => {
      pc.setVideoQuality(quality);
    });
  }, []);

  useEffect(() => {
    return () => {
      closeAll();
    };
  }, [closeAll]);

  return {
    setLocalStream,
    initiateOffer,
    handleOffer,
    handleAnswer,
    handleIceCandidate,
    startGuestStats,
    setPeerQuality,
    setAllPeersQuality,
    closePeer,
    closeAll,
  };
}
